// Ch76 · the Instagram DM ledger on a screen.
//
// So Grant can SEE DMs being answered without a seat reading JSON: summary
// counts (inbound / replied / failed, 24h), the last inbound and reply
// times, the two heartbeats the DM path writes (including the token identity
// probe from PR #477), and the latest five rows — hashed sender, 40-char
// preview, reply status. Backend contract honoured on every figure:
//
//   a number            -> the number
//   the literal unknown -> the WORD unknown, amber. Never a zero in its place.
//
// Self-fetching, like AdminTruthBand: it must not join the dashboard's
// Promise.all.

import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import {
  fetchInstagramDmLedger,
  type DmHeartbeat,
  type DmLedgerResponse,
  type DmMessage,
} from "../../services/instagramDmLedgerService";

const ROWS = 5;

export function isUnknown(value: unknown): boolean {
  return value === "unknown" || value === undefined;
}

export function formatCount(value: number | "unknown" | undefined): string {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "unknown";
}

export function formatWhen(value: string | null | "unknown" | undefined): string {
  if (value === null) return "never";
  if (typeof value !== "string" || value === "unknown") return "unknown";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "unknown" : d.toLocaleString();
}

function Figure({ label, value, testId }: { label: string; value: string; testId: string }) {
  const unknown = value === "unknown";
  return (
    <Box data-testid={testId}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} sx={{ color: unknown ? "warning.main" : "text.primary", lineHeight: 1.3 }}>
        {value}
      </Typography>
    </Box>
  );
}

const HEARTBEAT_LABELS: Record<string, string> = {
  instagram_reply_worker: "reply worker",
  instagram_token_expiry: "token check",
};

export function heartbeatWord(hb: DmHeartbeat | undefined): { word: string; tone: "ok" | "bad" | "unknown" } {
  if (!hb || hb.status === "unknown") return { word: "unknown", tone: "unknown" };
  if (hb.status === "ok") return { word: "ok", tone: "ok" };
  if (hb.status === "fail") return { word: "failing", tone: "bad" };
  return { word: "unknown", tone: "unknown" };
}

export function identityWord(hb: DmHeartbeat | undefined): { word: string; tone: "ok" | "bad" | "unknown" } {
  const v = hb?.token_identity;
  if (v === "ok") return { word: "ok", tone: "ok" };
  if (v === "red") return { word: "red", tone: "bad" };
  return { word: "unknown", tone: "unknown" };
}

const TONE_COLOR = { ok: "success.main", bad: "error.main", unknown: "warning.main" } as const;

function HeartbeatLine({ jobName, hb }: { jobName: string; hb: DmHeartbeat | undefined }) {
  const beat = heartbeatWord(hb);
  const showIdentity = jobName === "instagram_token_expiry";
  const identity = identityWord(hb);
  return (
    <Box data-testid={`dm-heartbeat-${jobName}`} display="flex" gap={1} alignItems="baseline" flexWrap="wrap">
      <Typography variant="body2" color="text.secondary">
        {HEARTBEAT_LABELS[jobName] || jobName}:
      </Typography>
      <Typography variant="body2" fontWeight={700} sx={{ color: TONE_COLOR[beat.tone] }} data-testid={`dm-heartbeat-${jobName}-status`}>
        {beat.word}
      </Typography>
      {hb?.last_ok_at && (
        <Typography variant="caption" color="text.disabled">
          last ok {formatWhen(hb.last_ok_at)}
        </Typography>
      )}
      {beat.tone === "bad" && hb?.last_error && (
        <Typography variant="caption" color="text.disabled">
          {hb.last_error}
        </Typography>
      )}
      {showIdentity && (
        <>
          <Typography variant="body2" color="text.secondary">
            · token identity:
          </Typography>
          <Typography variant="body2" fontWeight={700} sx={{ color: TONE_COLOR[identity.tone] }} data-testid="dm-token-identity">
            {identity.word}
          </Typography>
          {hb?.token_identity_checked_at && (
            <Typography variant="caption" color="text.disabled" data-testid="dm-token-identity-checked">
              checked {formatWhen(hb.token_identity_checked_at)}
            </Typography>
          )}
          {identity.tone !== "ok" && hb?.token_identity_reason && (
            <Typography variant="caption" color="text.disabled">
              {hb.token_identity_reason}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

export function replyWord(m: DmMessage): { word: string; tone: "ok" | "bad" | "unknown" | "muted" } {
  const s = m.reply?.status;
  if (s === "sent") return { word: "sent", tone: "ok" };
  if (s === "queued") return { word: "queued", tone: "muted" };
  if (s === "failed") return { word: `failed${m.reply.error_class ? ` · ${m.reply.error_class}` : ""}`, tone: "bad" };
  if (s === "none") return { word: "no reply", tone: "muted" };
  return { word: "unknown", tone: "unknown" };
}

const InstagramDmLedgerCard: React.FC = () => {
  const [ledger, setLedger] = React.useState<DmLedgerResponse | null>(null);
  const [state, setState] = React.useState<"loading" | "ok" | "error">("loading");

  const load = React.useCallback(async () => {
    setState("loading");
    try {
      setLedger(await fetchInstagramDmLedger(ROWS));
      setState("ok");
    } catch {
      setLedger(null);
      setState("error");
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const summary = ledger?.summary;
  const messages = ledger?.messages;

  return (
    <Card sx={{ mb: 3 }} data-testid="dm-ledger-card">
      <CardContent>
        <Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
          <Typography variant="overline" color="text.secondary" fontWeight={700}>
            Instagram DMs — answered by the machine
          </Typography>
          {state === "ok" && ledger && (
            <Typography variant="caption" color="text.disabled">
              as of {new Date(ledger.computed_at).toLocaleString()}
            </Typography>
          )}
        </Box>

        {state === "loading" && (
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            {[0, 1, 2].map((i) => (
              <Grid item xs={4} md={2} key={i}>
                <Skeleton width={90} height={18} />
                <Skeleton width={50} height={28} />
              </Grid>
            ))}
          </Grid>
        )}

        {state === "error" && (
          <Box mt={1} display="flex" alignItems="center" gap={1.5}>
            <Typography variant="body2" color="warning.main" data-testid="dm-ledger-error">
              Couldn't read the DM ledger. Nothing here is being estimated in its place.
            </Typography>
            <Button size="small" onClick={() => void load()}>
              Retry
            </Button>
          </Box>
        )}

        {state === "ok" && ledger && summary && (
          <>
            <Grid container spacing={2} sx={{ mt: 0.25 }}>
              <Grid item xs={4} md={2}>
                <Figure label="Inbound 24h" value={formatCount(summary.inbound_24h)} testId="dm-inbound-24h" />
              </Grid>
              <Grid item xs={4} md={2}>
                <Figure label="Replied 24h" value={formatCount(summary.replied_24h)} testId="dm-replied-24h" />
              </Grid>
              <Grid item xs={4} md={2}>
                <Figure label="Failed 24h" value={formatCount(summary.failed_24h)} testId="dm-failed-24h" />
              </Grid>
              <Grid item xs={6} md={3}>
                <Figure label="Last inbound" value={formatWhen(summary.last_inbound_at)} testId="dm-last-inbound" />
              </Grid>
              <Grid item xs={6} md={3}>
                <Figure label="Last reply" value={formatWhen(summary.last_reply_at)} testId="dm-last-reply" />
              </Grid>
            </Grid>

            <Box mt={1.5} display="flex" flexDirection="column" gap={0.5}>
              {["instagram_reply_worker", "instagram_token_expiry"].map((job) => (
                <HeartbeatLine key={job} jobName={job} hb={ledger.heartbeats[job]} />
              ))}
            </Box>

            <Box mt={1.5} sx={{ overflowX: "auto" }}>
              {messages === "unknown" || !Array.isArray(messages) ? (
                <Typography variant="body2" sx={{ color: "warning.main" }} data-testid="dm-messages-unknown">
                  latest messages: unknown (messages table could not be read)
                </Typography>
              ) : messages.length === 0 ? (
                <Typography variant="body2" color="text.secondary" data-testid="dm-messages-empty">
                  No inbound DMs recorded yet.
                </Typography>
              ) : (
                <Table size="small" data-testid="dm-messages">
                  <TableHead>
                    <TableRow>
                      <TableCell>received</TableCell>
                      <TableCell>sender</TableCell>
                      <TableCell>preview</TableCell>
                      <TableCell>reply</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {messages.slice(0, ROWS).map((m) => {
                      const reply = replyWord(m);
                      return (
                        <TableRow key={m.id} data-testid={`dm-row-${m.id}`}>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{formatWhen(m.received_at)}</TableCell>
                          <TableCell sx={{ fontFamily: "monospace" }}>{m.sender_hash}</TableCell>
                          <TableCell>{m.preview}</TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              color: reply.tone === "muted" ? "text.secondary" : TONE_COLOR[reply.tone],
                            }}
                          >
                            {reply.word}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default InstagramDmLedgerCard;
